import { zodResolver } from '@hookform/resolvers/zod';
import { AlertTriangle, Info } from 'lucide-react';
import {
  lazy,
  Suspense,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useAccount, useSwitchChain } from 'wagmi';
import { z } from 'zod';
import { useShallow } from 'zustand/react/shallow';

import { ArrowLeft, OctagonAlert } from '@/components/icons/barrel';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { Button, ButtonProps } from '@/components/ui/button';
import {
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import TooltipWrapper, {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import config from '@/config';
import { useFetchAccount } from '@/features/account/api/get-account';
import { UnitSelect } from '@/features/trade/order/components/unit-select';
import { useCapitalPlans } from '@/features/vest-capital/api/get-capital-plans';
import {
  getIsStatusBlocked,
  getIsStatusEval,
  getIsStatusFunded,
  getIsStatusFundedEnd,
} from '@/features/vest-capital/utils/status';
import { useIsDepositGeoBlocked, usePrivyAccount } from '@/hooks';
import useEvmBalance from '@/hooks/use-evm-balance';
import useFundedAccount from '@/hooks/use-funded-account';
import usePrivyFunding from '@/hooks/use-privy-funding';
import useSolanaBalance from '@/hooks/use-solana-balance';
import useCreateDeposit from '@/lib/api/create-deposit';
import useCreateFundedWithdraw from '@/lib/api/create-funded-withdraw';
import useSolanaDeposit from '@/lib/api/create-solana-deposit';
// eslint-disable-next-line import/no-cycle
import { useSolanaWithdraw } from '@/lib/api/create-solana-withdraw';
import useCreateV2Withdraw from '@/lib/api/create-v2-withdraw';
import useCreateWithdraw from '@/lib/api/create-withdraw';
import { cn } from '@/lib/utils';
import { defaultChain, getChainDecimals } from '@/lib/wagmi/config';
import { useAccountStore, useCurrentAccount } from '@/store/use-account-store';
import { COLLATERAL_DECIMALS } from '@/store/use-markets-store';
import { useModalStore } from '@/store/use-modal-store';
import {
  FilledTransfer,
  NewTransfer,
  useTransfersStore,
} from '@/store/use-transfers-store';
import {
  CapitalStatus,
  NetworkType,
  OrderStatus,
  TransferType,
} from '@/types/enums';
import { truncateAddress } from '@/utils/format';
import {
  formatBigInt,
  formatNumber,
  parseDecimalToBigInt,
} from '@/utils/value-format';

const transferFormSchema = z.object({
  size: z.string().refine((v) => +v >= 1, 'transferDialog.minTransferAmount'),
  // size: z.string(),
  chainId: z.number(),
  recipient: z.string().optional(),
});

export type TransferFormValues = z.infer<typeof transferFormSchema>;

interface TransferDialogProps extends ButtonProps {
  children?: ReactNode;
  orderType: TransferType.DEPOSIT | TransferType.WITHDRAW;
  isOpen?: boolean;
  balance?: bigint;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
}

const LazyTransferDialogContent = lazy(() =>
  Promise.resolve({
    default: TransferDialogContent,
  }),
);

const MAX_MINT_AMOUNT = 10_000;
const MAX_MINT_AMOUNT_BIGINT = parseDecimalToBigInt(
  MAX_MINT_AMOUNT.toString(),
  COLLATERAL_DECIMALS,
);

export const WITHDRAW_DELAY_HOURS = 12;
export const WITHDRAW_THRESHOLD_AMOUNT = 50_000;
export const WITHDRAW_THRESHOLD_HOURS = 24;

const THRESHOLD_AMOUNT =
  BigInt(WITHDRAW_THRESHOLD_AMOUNT) * 10n ** COLLATERAL_DECIMALS;

const { isDevelopment } = config;

export const TransferDialog = ({
  children,
  orderType,
  isOpen: externalIsOpen,
  onOpenChange = () => {},
  balance,
  hideTrigger,
  className,
  ...props
}: TransferDialogProps) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isControlled = externalIsOpen !== undefined;
  const isOpen = isControlled ? externalIsOpen : internalIsOpen;
  const { t } = useTranslation();

  // const { isEval, isFundedOrEval } = useFundedAccount();

  useEffect(() => {
    if (isControlled) {
      setInternalIsOpen(externalIsOpen);
    }
  }, [externalIsOpen, isControlled]);

  const address = useAccountStore((state) => state.address);
  const { isEmbedded, networkType } = usePrivyAccount();
  const { address: evmAddress } = useAccount();

  const isWrongAddress =
    !isEmbedded &&
    !!address &&
    !!evmAddress &&
    networkType === NetworkType.EVM &&
    evmAddress !== address;

  const handleOpenChange = (newIsOpen: boolean) => {
    if (isWrongAddress && orderType === TransferType.DEPOSIT) {
      useModalStore.setState({ isSwitchModalOpen: true });
    } else {
      if (!isControlled) {
        setInternalIsOpen(newIsOpen);
      }
      onOpenChange?.(newIsOpen);
    }
  };

  useEffect(() => {
    if (!isWrongAddress || orderType !== TransferType.DEPOSIT) return;
    setInternalIsOpen(false);
    onOpenChange?.(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isWrongAddress, orderType]);

  const capitalStatus = useAccountStore((state) =>
    state.getCapitalStatus(state.address),
  );

  const isDisabled = useMemo(() => {
    if (getIsStatusEval(capitalStatus)) return true;
    if (getIsStatusFunded(capitalStatus)) {
      switch (orderType) {
        case TransferType.DEPOSIT:
          return true;
        case TransferType.WITHDRAW:
          return (
            getIsStatusFundedEnd(capitalStatus) ||
            getIsStatusBlocked(capitalStatus)
          );
      }
    }
    return false;
  }, [capitalStatus, orderType]);

  const { isGeoBlocked } = useIsDepositGeoBlocked();
  const isDepositGeoBlocked =
    orderType === TransferType.DEPOSIT && isGeoBlocked;
  const isTriggerDisabled = isDisabled || isDepositGeoBlocked;

  return (
    <>
      {!hideTrigger && (
        <TooltipWrapper
          disabled={!isTriggerDisabled}
          align="center"
          direction="bottom"
          content={
            isDepositGeoBlocked
              ? t('transferDialog.geoBlockedTooltip')
              : t('transferDialog.disabledTooltip', {
                  action:
                    orderType === TransferType.DEPOSIT
                      ? config.isDevelopment
                        ? t('transferDialog.getFunds')
                        : t('transferDialog.deposit')
                      : t('transferDialog.withdraw'),
                })
          }
          className="hidden md:block"
        >
          {children || (
            <Button
              variant={
                orderType === TransferType.DEPOSIT ? 'contrast' : 'secondary'
              }
              size="xl"
              className={cn('h-12 w-full capitalize', className)}
              disabled={isTriggerDisabled}
              onClick={() => handleOpenChange(true)}
              {...props}
            >
              {orderType === TransferType.DEPOSIT
                ? config.isDevelopment
                  ? t('transferDialog.getFunds')
                  : t('transferDialog.deposit')
                : capitalStatus !== CapitalStatus.UNKNOWN &&
                    capitalStatus !== CapitalStatus.NOT_ELIGIBLE
                  ? t('transferDialog.claimProfit')
                  : t('transferDialog.withdraw')}
            </Button>
          )}
        </TooltipWrapper>
      )}
      <Dialog open={isOpen} onOpenChange={handleOpenChange}>
        {(isOpen || orderType === TransferType.DEPOSIT) && (
          <Suspense fallback={null}>
            <LazyTransferDialogContent
              isOpen={isOpen}
              onOpenChange={handleOpenChange}
              orderType={orderType}
              balance={balance}
            />
          </Suspense>
        )}
      </Dialog>
    </>
  );
};

function TransferDialogContent({
  orderType,
  isOpen,
  onOpenChange = () => {},
  balance: forcedBalance,
}: TransferDialogProps) {
  const [isNetworkWarning, setIsNetworkWarning] = useState(false);
  const { t } = useTranslation();

  const { chain: _chain } = useAccount();

  const [balance, setBalance] = useState(forcedBalance ?? 0n);
  const [showFundedTooltip, setShowFundedTooltip] = useState<boolean>(false);

  const { isStatusLoading } = useFundedAccount();
  const {
    address: primaryAddress,
    networkType,
    isEmbedded,
  } = usePrivyAccount();

  const { address, capitalStatus } = useAccountStore(
    useShallow((state) => ({
      address: state.address as `0x${string}`,
      capitalStatus: state.getCapitalStatus(state.address),
    })),
  );

  const form = useForm<TransferFormValues>({
    resolver: zodResolver(transferFormSchema),
    mode: 'onChange',
    defaultValues: {
      size: '',
      chainId:
        config.isDevelopment || capitalStatus === CapitalStatus.FUNDED
          ? defaultChain.id
          : _chain?.id ?? defaultChain.id,
      recipient:
        orderType === TransferType.WITHDRAW
          ? capitalStatus === CapitalStatus.FUNDED && primaryAddress
            ? primaryAddress
            : ''
          : undefined,
    },
  });

  const shouldFetchBalance =
    !isDevelopment &&
    isOpen &&
    orderType !== TransferType.WITHDRAW &&
    forcedBalance === undefined;

  // const [isSwitchingChain, setIsSwitchingChain] = useState(false);

  const chainId = form.watch('chainId');

  const { chains, switchChain, isPending: isSwitchingChain } = useSwitchChain();

  const evmBalance = useEvmBalance({
    chainId,
    disabled: !shouldFetchBalance || networkType !== NetworkType.EVM,
  });

  const solanaBalance = useSolanaBalance({
    disabled: !shouldFetchBalance,
  });

  useEffect(() => {
    if (!evmBalance.isEnabled) return;
    setBalance(evmBalance.value);
  }, [evmBalance.value, evmBalance.isEnabled]);

  useEffect(() => {
    if (!solanaBalance.isEnabled) return;
    setBalance(solanaBalance.value);
  }, [solanaBalance.value, solanaBalance.isEnabled]);

  const { accountValue, availableFunds } = useCurrentAccount((state) => {
    let availableFunds = state.availableFunds.bigint - state.nonWithdrawable;
    if (availableFunds < 0n) {
      availableFunds = 0n;
    }
    return {
      accountValue: state.value.bigint,
      availableFunds,
    };
  });

  useEffect(() => {
    if (
      orderType !== TransferType.WITHDRAW ||
      forcedBalance !== undefined ||
      capitalStatus === CapitalStatus.FUNDED
    )
      return;
    setBalance(availableFunds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableFunds, forcedBalance === undefined, orderType, capitalStatus]);

  const { data: accountResponse, isPending: isAccountPending } =
    useFetchAccount();
  const { data: capitalPlans } = useCapitalPlans();

  useEffect(() => {
    setShowFundedTooltip(false);

    if (
      orderType !== TransferType.WITHDRAW ||
      capitalStatus !== CapitalStatus.FUNDED
    )
      return;
    if (
      !capitalPlans ||
      !accountResponse?.capital?.activeCapitalPlan ||
      !address
    ) {
      setBalance(0n);
    } else {
      const { activeCapitalPlan } = accountResponse.capital;
      const activePlan = capitalPlans.find(
        (plan) => plan.name === activeCapitalPlan,
      );
      if (!activePlan) {
        setBalance(0n);
      } else {
        const initialBalance = parseDecimalToBigInt(
          activePlan.metadata.account_size,
          COLLATERAL_DECIMALS - 2n, // NOTE: divided by 100
        );
        const newBalance = availableFunds - initialBalance;

        if (newBalance < 0n) {
          setShowFundedTooltip(true);
          setBalance(0n);
        } else {
          setBalance(newBalance);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    orderType,
    address,
    availableFunds,
    capitalStatus,
    accountResponse?.capital?.activeCapitalPlan,
    capitalPlans,
  ]);

  const size = form.watch('size');
  const recipient = form.watch('recipient');

  const chainDecimals =
    orderType === TransferType.DEPOSIT
      ? getChainDecimals(chainId)
      : COLLATERAL_DECIMALS;

  const baseSize = parseDecimalToBigInt(
    size.endsWith('.') ? `${size}00` : size,
    chainDecimals,
  );

  const { depositForBurn } = useSolanaDeposit();
  const { withdraw: createSolanaWithdraw } = useSolanaWithdraw(true);

  const { setValue } = form;

  useEffect(() => {
    if (config.isDevelopment || orderType !== TransferType.DEPOSIT) return;
    if (networkType === NetworkType.SOLANA) {
      setValue('chainId', 0);
    } else if (_chain?.id) {
      setValue('chainId', _chain.id);
    }
  }, [_chain?.id, networkType, orderType, setValue]);

  // Default recipient to address if external wallet
  useEffect(() => {
    if (!address || isEmbedded || orderType === TransferType.DEPOSIT) return;
    setValue('recipient', address);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEmbedded, address, orderType]);

  const { mutateAsync: createWithdraw } = useCreateWithdraw();
  const { mutateAsync: createV2Withdraw } = useCreateV2Withdraw();
  const { mutateAsync: createFundedWithdraw } = useCreateFundedWithdraw();
  const { createDeposit } = useCreateDeposit();

  const { fundWallet } = usePrivyFunding({
    amountNeeded: baseSize,
    onBalanceChange: (newBalance) => setBalance(newBalance),
  });

  const { isGeoBlocked } = useIsDepositGeoBlocked();
  const isDepositGeoBlocked =
    orderType === TransferType.DEPOSIT && isGeoBlocked;

  const onSubmit = async (data: TransferFormValues, balance: bigint) => {
    if (isDepositGeoBlocked) return;
    if (orderType === TransferType.WITHDRAW && !isNetworkWarning) {
      setIsNetworkWarning(true);
    } else {
      const size = parseDecimalToBigInt(
        data.size.endsWith('.') ? `${data.size}00` : data.size,
        getChainDecimals(data.chainId),
      );

      if (
        !isDevelopment &&
        orderType === TransferType.DEPOSIT &&
        size > balance &&
        networkType === NetworkType.EVM
      ) {
        if (!address) return;
        await fundWallet({
          amount: formatBigInt(size - balance, COLLATERAL_DECIMALS, {
            digits: 2,
            round: 'ceil',
          }),
        });
      } else if (orderType === TransferType.DEPOSIT) {
        switch (networkType) {
          case NetworkType.EVM:
            await createDeposit(
              {
                size,
                chainId: data.chainId,
              },
              { waitForConfirm: true, showToast: true },
            );
            break;
          case NetworkType.SOLANA:
            await depositForBurn(data.size);
            break;
        }
        form.resetField('size');
      } else {
        switch (networkType) {
          case NetworkType.EVM:
            if (capitalStatus === CapitalStatus.FUNDED) {
              await createFundedWithdraw({ size }).catch((err: Error) => {
                if (!err.message) return;
                toast.error(err.message);
              });
            } else {
              const authParams = useAccountStore.getState().getAuthParams();
              if (authParams?.accountGroup === undefined)
                return toast.error('Auth params not found. Please register.');
              if (authParams.accountGroup === 2) {
                await createV2Withdraw({
                  chainId: data.chainId,
                  recipient: data.recipient,
                  execute: true,
                  size,
                });
              } else {
                await createWithdraw({
                  chainId: data.chainId,
                  recipient: data.recipient,
                  execute: true,
                  size,
                });
              }
            }
            break;
          case NetworkType.SOLANA:
            createSolanaWithdraw(data);
            break;
        }
        form.resetField('size');
      }
      onOpenChange(false);
    }
  };

  const { isSubmitting, isValid } = form.formState;

  const bigIntSize = parseDecimalToBigInt(size, COLLATERAL_DECIMALS);

  const notEnoughFunds =
    orderType === TransferType.WITHDRAW && bigIntSize > balance;

  const cumulatedDaySize = useTransfersStore((state) => {
    if (orderType === TransferType.DEPOSIT) return 0n;
    const currentTime = useAccountStore.getState().getAccurateTime();
    const cumulatedAmount = Object.values(state.getTransfers(address))
      .filter(
        (transfer) =>
          transfer.orderType === TransferType.WITHDRAW &&
          transfer.status !== OrderStatus.REJECTED &&
          currentTime - transfer.postTime <= 24 * 60 * 60 * 1000,
      )
      .reduce(
        (prev, transfer) =>
          prev + (transfer as NewTransfer | FilledTransfer).size,
        0n,
      );
    return cumulatedAmount;
  });

  const isTimeLonger = bigIntSize + cumulatedDaySize > THRESHOLD_AMOUNT;

  // const instantLeft =
  //   cumulatedDaySize > THRESHOLD_AMOUNT
  //     ? 0n
  //     : THRESHOLD_AMOUNT - cumulatedDaySize;

  const available = useMemo(() => {
    if (!isDevelopment || orderType !== TransferType.DEPOSIT) return balance;
    if (isAccountPending) return 0n;
    const limitDiff = MAX_MINT_AMOUNT_BIGINT - accountValue;
    return limitDiff < 0n ? 0n : limitDiff;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [balance, isDevelopment, orderType, isAccountPending, accountValue]);

  const chainName =
    networkType === NetworkType.SOLANA
      ? 'Solana'
      : chains.find((chain) => chain.id === chainId)?.name ?? '-';

  // const isFundedTimeWrong =
  //   !isLocal &&
  //   !isDevelopment &&
  //   capitalStatus === CapitalStatus.FUNDED &&
  //   !!accountResponse?.capital?.capitalActivatedAt &&
  //   getAccurateTime() -
  //     new Date(accountResponse.capital.capitalActivatedAt).getTime() <
  //     48 * 60 * 60 * 1_000;

  // const isFundedDisabled = isFundedTimeWrong || isFundedOpenPositions;
  const isDisabled = (() => {
    if (!isValid || isSubmitting || parseFloat(size) === 0 || notEnoughFunds)
      return true;
    if (orderType === TransferType.WITHDRAW) {
      if (isStatusLoading) return true;
      if (capitalStatus !== CapitalStatus.FUNDED && !recipient) return true;
    }
    if (networkType === NetworkType.SOLANA && recipient?.startsWith('0x'))
      return true;
    return false;
  })();

  const submitButtonText = useMemo(() => {
    if (isSubmitting) {
      return orderType === TransferType.DEPOSIT
        ? t('transferDialog.confirming')
        : t('transferDialog.withdrawButton');
    }
    return orderType === TransferType.DEPOSIT
      ? isDevelopment
        ? t('transferDialog.getFundsButton')
        : t('transferDialog.depositButton')
      : t('transferDialog.withdrawButton');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, orderType, isDevelopment, t]);

  return (
    <DialogContent
      className={cn(
        'gap-0 border border-border bg-background p-0 sm:max-w-md sm:rounded-none',
      )}
    >
      <FormProvider {...form}>
        <form
          onSubmit={form.handleSubmit(
            (data) => onSubmit(data, balance),
            (errors) => {
              console.error('Form validation errors:', errors);
            },
          )}
        >
          <DialogHeader className="flex flex-row items-center gap-3 space-y-0 px-5 pt-6">
            {isNetworkWarning && (
              <button
                className="relative bottom-px"
                type="button"
                onClick={() => setIsNetworkWarning(false)}
              >
                <ArrowLeft size={18} />
              </button>
            )}
            <DialogTitle className="text-xl text-foreground">
              {orderType === TransferType.DEPOSIT
                ? isDevelopment
                  ? t('transferDialog.getFunds')
                  : t('transferDialog.deposit')
                : isNetworkWarning
                  ? capitalStatus === CapitalStatus.FUNDED
                    ? 'Confirm'
                    : t('transferDialog.confirmNetwork')
                  : capitalStatus === CapitalStatus.FUNDED
                    ? 'Profit Withdraw'
                    : t('transferDialog.withdraw')}
            </DialogTitle>
          </DialogHeader>
          {isNetworkWarning ? (
            <div className="space-y-4 px-5 pb-6 pt-10">
              <OctagonAlert size={48} className="mx-auto" />
              <h3 className="mx-auto max-w-64 text-center text-xl font-medium">
                {capitalStatus === CapitalStatus.FUNDED
                  ? 'This is an internal transfer to your main account.'
                  : t('transferDialog.networkSupportQuestion', { chainName })}
              </h3>
              <p className="text-center text-muted-foreground">
                {capitalStatus === CapitalStatus.FUNDED
                  ? 'You are going to receive 90% of the amount you withdraw due to profit sharing agreement.'
                  : t('transferDialog.networkWarning')}
              </p>
              <div>
                <div className="flex h-10 items-center justify-between">
                  <p className="text-base text-vestgrey-100">
                    {t('transferDialog.amount')}
                  </p>
                  <p className="text-font flex font-mono">
                    {formatNumber(size, { digits: 2 })}
                  </p>
                </div>
                {capitalStatus === CapitalStatus.FUNDED && (
                  <div className="flex h-10 items-center justify-between">
                    <p className="text-base text-vestgrey-100">
                      You will receive
                    </p>
                    <p className="text-font flex font-mono">
                      {formatNumber(Number(size) * 0.9, { digits: 2 })}
                    </p>
                  </div>
                )}
                <div className="flex h-10 items-center justify-between">
                  <p className="text-base text-vestgrey-100">
                    {t('transferDialog.recipient')}
                  </p>
                  <p className="text-font flex font-mono">
                    {recipient ? truncateAddress(recipient) : '-'}
                  </p>
                </div>
                {capitalStatus !== CapitalStatus.FUNDED && (
                  <div className="flex h-10 items-center justify-between">
                    <p className="text-base text-vestgrey-100">
                      {t('transferDialog.network')}
                    </p>
                    <p className="text-font flex font-mono">{chainName}</p>
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5 px-5 pb-6 pt-4">
              {!config.isDevelopment && // don't show in dev and for Solana
                networkType !== NetworkType.SOLANA &&
                capitalStatus !== CapitalStatus.FUNDED && (
                  <FormField
                    control={form.control}
                    name="chainId"
                    render={({ field }) => (
                      <FormItem className="mb-3">
                        <FormLabel required>
                          {t('transferDialog.network')}
                        </FormLabel>
                        <Select
                          disabled={isSwitchingChain}
                          value={field.value?.toString()}
                          onValueChange={(value) => {
                            switch (orderType) {
                              case TransferType.DEPOSIT:
                                switchChain({
                                  chainId: Number(value),
                                });
                                break;
                              case TransferType.WITHDRAW:
                                field.onChange(Number(value));
                                break;
                            }
                          }}
                        >
                          <SelectTrigger
                            className={cn(
                              'h-10 rounded-none border-0 bg-vestgrey-800 hover:bg-border',
                              !field.value && 'text-muted-foreground',
                            )}
                          >
                            <SelectValue
                              placeholder={t('transferDialog.selectNetwork')}
                            />
                          </SelectTrigger>
                          <SelectContent>
                            {chains.map((chain) => (
                              <SelectItem
                                value={chain.id.toString()}
                                key={chain.id}
                              >
                                {chain.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                )}
              {orderType === TransferType.WITHDRAW && (
                <FormField
                  control={form.control}
                  name="recipient"
                  render={({ field }) => (
                    <FormItem className="mb-3">
                      <FormLabel required>
                        {t('transferDialog.recipient')}
                      </FormLabel>
                      <Input
                        {...field}
                        readOnly={capitalStatus === CapitalStatus.FUNDED}
                        disabled={capitalStatus === CapitalStatus.FUNDED}
                        className="rounded-none bg-vestgrey-800 font-mono text-base"
                        type="text"
                        placeholder={'0x...'}
                        value={
                          capitalStatus === CapitalStatus.FUNDED && field.value
                            ? `Primary Account (${truncateAddress(field.value)})`
                            : field.value
                        }
                      />
                      {networkType === NetworkType.SOLANA &&
                        field.value?.startsWith('0x') && (
                          <FormMessage className="text-sm">
                            {t('transferDialog.invalidSolanaAddress')}
                          </FormMessage>
                        )}
                    </FormItem>
                  )}
                />
              )}
              <Controller
                control={form.control}
                name="size"
                render={({ field, formState }) => (
                  <FormItem className="mb-3">
                    <FormLabel required>{t('transferDialog.amount')}</FormLabel>
                    <div className="grid grid-cols-[1fr_max-content] items-center gap-3">
                      <div className="flex">
                        <Input
                          {...field}
                          className="flex-1 rounded-none bg-vestgrey-800 font-mono text-base"
                          type="text"
                          value={field.value}
                          onBlur={(e) =>
                            e.target.value.endsWith('.') &&
                            field.onChange(e.target.value.slice(0, -1))
                          }
                          onChange={(e) => {
                            const inputValue = e.target.value.replace(/,/g, '');
                            const value = parseDecimalToBigInt(
                              inputValue,
                              chainDecimals,
                            );
                            if (
                              value <= available &&
                              (inputValue === '' ||
                                /^(\d+|\d*\.\d{0,2})$/.test(inputValue))
                            ) {
                              field.onChange(
                                inputValue.endsWith('.') &&
                                  size.length > inputValue.length
                                  ? inputValue.slice(0, -1)
                                  : inputValue,
                              );
                            }
                          }}
                        />
                        {!isEmbedded && (
                          <UnitSelect
                            defaultValue="USDC"
                            units={['USDC']}
                            className="[&>button]:px-3"
                          />
                        )}
                      </div>
                      <button
                        // className="h-full bg-vestgrey-800 px-3 uppercase"
                        className="h-10 bg-black-alt px-4 py-2 font-mono text-sm uppercase"
                        onClick={(e) => {
                          e.stopPropagation();
                          e.preventDefault();
                          form.setValue(
                            'size',
                            formatBigInt(available, chainDecimals, {
                              digits: 2,
                              commas: false,
                            }),
                            {
                              shouldValidate: true,
                            },
                          );
                        }}
                      >
                        {t('transferDialog.max')}
                      </button>
                    </div>

                    {formState.errors.size?.message && (
                      <FormMessage className="text-sm">
                        {t(formState.errors.size.message, {
                          amount: '1 USDC',
                        })}
                      </FormMessage>
                    )}
                  </FormItem>
                )}
              />
              {orderType === TransferType.WITHDRAW &&
                capitalStatus !== CapitalStatus.FUNDED && (
                  <div className="mb-3 flex gap-2 rounded-md border border-border bg-muted p-3">
                    <AlertTriangle size={14} className="shrink-0" />
                    <p className="text-sm text-foreground">
                      {t('transferDialog.withdrawalLimitInfo', {
                        threshold: formatNumber(WITHDRAW_THRESHOLD_AMOUNT, {
                          compact: true,
                          style: 'currency',
                        }),
                        thresholdHours: WITHDRAW_THRESHOLD_HOURS,
                        delayHours: WITHDRAW_DELAY_HOURS,
                      })}
                    </p>
                  </div>
                )}
              <div className="flex h-10 items-center justify-between">
                <p className="text-base text-vestgrey-100">
                  {t('transferDialog.available')}
                </p>
                <p className="text-font flex items-center gap-2 font-mono">
                  {formatBigInt(available, chainDecimals, { digits: 2 })}

                  {showFundedTooltip && (
                    <TooltipProvider>
                      <Tooltip delayDuration={0}>
                        <TooltipTrigger className="text-vestgrey-100">
                          <Info size={14} />
                        </TooltipTrigger>
                        <TooltipContent>
                          You must close open positions to withdraw profit
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                </p>
              </div>
              {orderType === TransferType.WITHDRAW &&
                capitalStatus !== CapitalStatus.FUNDED && (
                  <div className="flex h-10 items-center justify-between">
                    <p className="text-base text-vestgrey-100">
                      {t('transferDialog.withdrawnInLast', {
                        hours: WITHDRAW_THRESHOLD_HOURS,
                      })}
                    </p>
                    <p className="text-font flex font-mono">
                      {formatBigInt(cumulatedDaySize, COLLATERAL_DECIMALS, {
                        digits: 2,
                      })}
                    </p>
                  </div>
                )}
              <div className="flex h-10 items-center justify-between">
                <p className="text-base text-vestgrey-100">
                  {orderType === TransferType.DEPOSIT
                    ? t('transferDialog.fundingTime')
                    : t('transferDialog.withdrawalProcessingTime')}
                </p>
                <p className="text-font flex font-mono">
                  {orderType === TransferType.WITHDRAW &&
                    (networkType == NetworkType.SOLANA
                      ? t('transferDialog.fifteenMinutes')
                      : isTimeLonger
                        ? t('transferDialog.hours', {
                            hours: WITHDRAW_THRESHOLD_HOURS,
                          })
                        : capitalStatus === CapitalStatus.FUNDED
                          ? t('transferDialog.twentyFourHours')
                          : t('transferDialog.instant'))}
                  {orderType === TransferType.DEPOSIT &&
                    (networkType
                      ? t('transferDialog.twentySeconds')
                      : t('transferDialog.instant'))}
                </p>
              </div>
            </div>
          )}
          <DialogFooter>
            <div className="w-full">
              <TooltipWrapper
                disabled={!isDepositGeoBlocked}
                content={t('transferDialog.geoBlockedTooltip')}
                direction="top"
              >
                <Button
                  type="submit"
                  className="h-14 w-full rounded-none font-mono text-lg font-normal uppercase tracking-wider"
                  disabled={isDisabled || isDepositGeoBlocked}
                >
                  {/* {isBalanceLoading && (
                <Loader className="mr-3 w-5 animate-spin duration-2s" />
              )} */}
                  {notEnoughFunds
                    ? t('transferDialog.insufficientSize')
                    : !isDevelopment &&
                        orderType === TransferType.DEPOSIT &&
                        networkType === NetworkType.EVM &&
                        baseSize > balance
                      ? t('transferDialog.fund', {
                          amount: formatBigInt(
                            baseSize - balance,
                            COLLATERAL_DECIMALS,
                            { digits: 2, round: 'ceil' },
                          ),
                        })
                      : submitButtonText}
                </Button>
              </TooltipWrapper>
            </div>
          </DialogFooter>
        </form>
      </FormProvider>
    </DialogContent>
  );
}
